import React, { useMemo, useState, useContext, useEffect } from 'react';
import AppConst from '../AppLayout/const';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
// Components
import MultiSelect from '../MUI/MultiSelect';
// API
import {
  getAgendaFilter,
  getProgramFilter,
  getMaterialsFilter,
  getGroupFilter,
} from '../../api/filterOptionsApi';
import { exportMaterialUtilization } from '../../api/marketingMaterialApi';
const teams = ['KASS', 'SPARTAN'];

const labelSx = {
  fontSize: 12,
  fontWeight: 700,
  color: '#817657',
  letterSpacing: 0.5,
  mb: 0.7,
};

const selectAllSx = {
  fontSize: 11,
  fontWeight: 700,
  color: '#9a8252',
  letterSpacing: 0.3,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',

    '& fieldset': {
      borderColor: '#d8d3c5',
    },

    '&:hover fieldset': {
      borderColor: '#cfa45f',
    },

    '&.Mui-focused fieldset': {
      borderColor: '#cfa45f',
      borderWidth: 1,
    },
  },
};

const initialForm = {
  agendaIds: [],
  titleIds: [],
  materialIds: [],
  mdGroups: [],
  teams: [],
  dateFrom: moment().startOf('month').format('YYYY-MM-DD'),
  dateTo: moment().endOf('month').format('YYYY-MM-DD'),
};

const MaterialsUtilization = () => {
  const { toggleModal, onToggleModal } = useContext(AppConst.AppContext);
  const [agendaOptions, setAgendaOptions] = useState({ loading: true, items: [] });
  const [programOptions, setProgramOptions] = useState({ loading: true, items: [] });
  const [materialsOptions, setMaterialsOptions] = useState({ loading: true, items: [] });
  const [groupOptions, setGroupOptions] = useState([]);
  const [toggle, setToggle] = useState(false);
  const [form, setForm] = useState(initialForm);

  const handleCancel = () => {
    setForm(initialForm);
    onToggleModal(null);
    setToggle(false);
  };

  const loadGroupDropdown = async () => {
    try {
      const response = await getGroupFilter();
      const { code, data } = response.data;

      if (code !== '200') {
        throw new Error('Please contact dev if issue occurs');
      }

      const value = data.filter((row) => row.md_group !== null).map((row) => row.md_group);
      setGroupOptions(value);
    } catch (error) {
      toast.error(error?.message || 'Failed to load groups options');
    }
  };
  const loadAgendOptions = async () => {
    try {
      setAgendaOptions((prev) => ({
        ...prev,
        loading: true,
      }));

      const response = await getAgendaFilter();
      const { code, data } = response.data;

      if (code !== '200') {
        throw new Error('Please contact dev if issue occurs');
      }

      setAgendaOptions({
        loading: false,
        items: Array.isArray(data) ? data : [],
      });
    } catch (error) {
      setAgendaOptions({
        loading: false,
        items: [],
      });

      toast.error(error?.message || 'Failed to load agenda options');
    }
  };

  const loadProgramOptions = async () => {
    try {
      setProgramOptions((prev) => ({
        ...prev,
        loading: true,
      }));

      const request = {
        agenda_options_id: form.agendaIds,
      };
      const response = await getProgramFilter(request);
      const { code, data } = response.data;

      if (code !== '200') {
        throw new Error('Please contact dev if issue occurs');
      }

      setProgramOptions({
        loading: false,
        items: Array.isArray(data) ? data : [],
      });
    } catch (error) {
      setProgramOptions({
        loading: false,
        items: [],
      });

      toast.error(error?.message || 'Failed to load program options');
    }
  };

  const loadMaterialsOptions = async () => {
    try {
      setMaterialsOptions((prev) => ({
        ...prev,
        loading: true,
      }));

      const request = {
        agenda_options_id: form.agendaIds,
        program: form.titleIds,
      };

      const response = await getMaterialsFilter(request);
      const { code, data } = response.data;

      if (code !== '200') {
        throw new Error('Please contact dev if issue occurs');
      }

      setMaterialsOptions({
        loading: false,
        items: Array.isArray(data) ? data : [],
      });
    } catch (error) {
      setMaterialsOptions({
        loading: false,
        items: [],
      });

      toast.error(error?.message || 'Failed to load materials options');
    }
  };

  useEffect(() => {
    if (toggleModal?.url === 'report/material_util') {
      setToggle(true);
      loadAgendOptions();
      loadGroupDropdown();
    }
  }, [toggleModal]);

  useEffect(() => {
    if (!form.agendaIds.length) {
      setProgramOptions({
        loading: false,
        items: [],
      });
      return;
    }
    loadProgramOptions();
  }, [form.agendaIds]);

  useEffect(() => {
    if (!form.titleIds.length) {
      setMaterialsOptions({
        loading: false,
        items: [],
      });
      return;
    }
    loadMaterialsOptions();
  }, [form.titleIds]);

  const updateForm = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /*
   * --------------------------------------------
   * MD GROUP / TEAM
   * --------------------------------------------
   */

  const toggleArrayValue = (field, value) => {
    setForm((prev) => {
      const exists = prev[field].includes(value);

      return {
        ...prev,
        [field]: exists ? prev[field].filter((item) => item !== value) : [...prev[field], value],
      };
    });
  };

  const handleSubmit = async () => {
    try {
      const request = {
        agenda_id: form.agendaIds ?? [],
        program: form.titleIds ?? [],
        material_id: form.materialIds ?? [],
        group: form.mdGroups ?? [],
        teams: form.teams ?? [],
        date_from: form.dateFrom,
        date_to: form.dateTo,
      };

      const response = await exportMaterialUtilization(request);
      const { code, result, file_name } = response.data.response;

      if (code !== '200') {
        throw new Error('Please contact dev if issue occurs');
      }
      window.location.href = result;
      toast.success(`Successfully downloaded file: ${file_name}`);
      setForm(initialForm);
      handleCancel();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderSelectAll = (field, options, getValue, disabled = false) => {
    if (disabled || !options.length) {
      return null;
    }

    const allValues = options.map(getValue);
    const allSelected = allValues.every((value) => form[field].includes(value));

    return (
      <Typography sx={selectAllSx} onClick={() => updateForm(field, allSelected ? [] : allValues)}>
        {allSelected ? 'Clear All' : 'Select All'}
      </Typography>
    );
  };

  const hasAgenda = form.agendaIds.length > 0;
  const hasTitle = form.titleIds.length > 0;

  const canSubmit =
    hasAgenda && form.mdGroups.length > 0 && form.teams.length > 0 && form.dateFrom && form.dateTo;

  return (
    <Dialog
      open={toggle}
      onClose={handleCancel}
      fullWidth
      maxWidth='sm'
      scroll='paper'
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          maxHeight: '92vh',
        },
      }}
    >
      {/* =========================
          DIALOG HEADER
      ========================== */}

      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e0d5',
        }}
      >
        <Typography fontSize={19} fontWeight={700} color='#1d1d1d'>
          Log Sampling Entry
        </Typography>

        <IconButton
          onClick={handleCancel}
          size='small'
          sx={{
            color: '#8d9a9a',
            '&:hover': {
              backgroundColor: '#f5f3ee',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {/* =========================
          DIALOG CONTENT
      ========================== */}

      <DialogContent
        dividers
        sx={{
          px: 2.5,
          py: 2.5,
          borderTop: 0,
          borderBottom: 0,
        }}
      >
        {/* MAIN AGENDA */}

        <Box mb={2.2}>
          <MultiSelect
            label='MAIN AGENDA NAME'
            value={form.agendaIds}
            loading={agendaOptions.loading}
            options={agendaOptions.items}
            getOptionValue={(item) => item.id}
            getOptionLabel={(item) => item.description}
            onChange={(value) => updateForm('agendaIds', value)}
            placeholder='Select agenda(s)...'
          />
        </Box>

        {/* TITLE */}

        <Box mb={2.2}>
          <MultiSelect
            label='PROGRAM'
            value={form.titleIds}
            loading={programOptions.loading}
            options={programOptions.items}
            getOptionValue={(item) => item.program}
            getOptionLabel={(item) => item.program}
            onChange={(value) => updateForm('titleIds', value)}
            disabled={!hasAgenda}
            placeholder={hasAgenda ? 'Select programs(s)...' : 'Select agenda first...'}
            labelAction={renderSelectAll(
              'titleIds',
              programOptions.items,
              (item) => item.program,
              !hasAgenda,
            )}
          />
        </Box>

        {/* MATERIAL WARNING / SUCCESS */}

        {!hasAgenda || !hasTitle ? (
          <Alert
            severity='warning'
            sx={{
              mb: 2.2,
              borderRadius: '9px',
              bgcolor: '#fdf0e8',
              color: '#a65e3d',
              border: '1px solid #f2d4c0',
              fontSize: 13,

              '& .MuiAlert-icon': {
                color: '#ad542c',
              },
            }}
          >
            Materials require both a Main Agenda Name and a Program before they can be selected.
          </Alert>
        ) : (
          <Alert
            severity='success'
            sx={{
              mb: 2.2,
              borderRadius: '9px',
              bgcolor: '#eef7e9',
              color: '#63845b',
              border: '1px solid #d9ead1',
              fontSize: 12,

              '& .MuiAlert-icon': {
                color: '#4f8a3e',
              },
            }}
          >
            Agenda and Program are both set — the Materials dropdown is now populated for the field
            team.
          </Alert>
        )}

        {/* MATERIAL */}

        <Box
          sx={{
            p: 1.5,
            mb: 2.5,
            border: '1px dashed #d8d2c5',
            borderRadius: '10px',
          }}
        >
          <MultiSelect
            label='MATERIAL'
            value={form.materialIds}
            loading={materialsOptions.loading}
            options={materialsOptions.items}
            getOptionValue={(item) => item.id}
            getOptionLabel={(item) => item.title}
            onChange={(value) => updateForm('materialIds', value)}
            disabled={!hasAgenda || !hasTitle}
            placeholder={
              !hasAgenda || !hasTitle
                ? 'Select a Program to populate materials...'
                : 'Select materials...'
            }
            labelAction={renderSelectAll(
              'materialIds',
              materialsOptions.items,
              (item) => item.id,
              !hasAgenda || !hasTitle,
            )}
            renderOption={(option) => (
              <Box
                sx={{
                  flex: 1,
                  py: 0.4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Typography fontSize={12} fontWeight={700}>
                    {option.title}
                  </Typography>

                  <Typography fontSize={8} fontWeight={800} color='#a48740'>
                    {option.program}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    mt: 0.2,
                  }}
                >
                  <Typography fontSize={9} color='text.secondary'>
                    {option.program_code}
                  </Typography>
                  <Typography fontSize={9} color='text.secondary'>
                    • {option.file_name}
                  </Typography>

                  <Typography fontSize={8} fontWeight={700} color='#6a8d58'>
                    • {option.status}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>

        {/* MD GROUP */}

        <Box mb={2.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ ...labelSx, mb: 0 }}>MD GROUP</Typography>

            {renderSelectAll('mdGroups', groupOptions, (group) => group)}
          </Box>

          <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mt: 0.7 }}>
            {groupOptions.map((group) => {
              const selected = form.mdGroups.includes(group);

              return (
                <Button
                  key={group}
                  variant='outlined'
                  onClick={() => toggleArrayValue('mdGroups', group)}
                  sx={{
                    minWidth: 0,
                    px: 1.7,
                    py: 0.65,
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontSize: 13,
                    fontWeight: 700,

                    color: selected ? '#fff' : '#67685f',

                    bgcolor: selected ? '#9a8252' : '#fff',

                    borderColor: selected ? '#9a8252' : '#d9d4c7',

                    '&:hover': {
                      bgcolor: selected ? '#8b7549' : '#faf8f2',
                    },
                  }}
                >
                  {group}
                </Button>
              );
            })}
          </Stack>
        </Box>

        {/* TEAMS */}

        <Box mb={2.5}>
          <Typography sx={labelSx}>TEAMS</Typography>

          <Stack direction='row' gap={1}>
            {teams.map((team) => {
              const selected = form.teams.includes(team);

              return (
                <Button
                  key={team}
                  variant='outlined'
                  onClick={() => toggleArrayValue('teams', team)}
                  sx={{
                    minWidth: 0,
                    px: 1.8,
                    py: 0.65,
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontSize: 13,
                    fontWeight: 700,

                    color: selected ? '#fff' : '#67685f',

                    bgcolor: selected ? '#9a8252' : '#fff',

                    borderColor: selected ? '#9a8252' : '#d9d4c7',

                    '&:hover': {
                      bgcolor: selected ? '#8b7549' : '#faf8f2',
                    },
                  }}
                >
                  {team}
                </Button>
              );
            })}
          </Stack>
        </Box>

        {/* DATES */}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
            },
            gap: 1.8,
          }}
        >
          <TextField
            fullWidth
            label='DATE FROM'
            type='date'
            value={form.dateFrom}
            onChange={(event) => updateForm('dateFrom', event.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={inputSx}
          />

          <TextField
            fullWidth
            label='DATE TO'
            type='date'
            value={form.dateTo}
            onChange={(event) => updateForm('dateTo', event.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={inputSx}
          />
        </Box>
      </DialogContent>

      {/* =========================
          DIALOG FOOTER
      ========================== */}

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid #e5e0d5',
          gap: 1.2,
        }}
      >
        <Button
          variant='outlined'
          onClick={handleCancel}
          sx={{
            minWidth: 102,
            borderRadius: '9px',
            borderColor: '#d8d3c5',
            color: '#656960',
            fontWeight: 700,
            textTransform: 'uppercase',

            '&:hover': {
              borderColor: '#bdb6a5',
            },
          }}
        >
          Cancel
        </Button>

        <Button
          variant='contained'
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={{
            minWidth: 148,
            borderRadius: '9px',
            bgcolor: '#2e7d32',
            color: '#fff',
            fontWeight: 550,
            textTransform: 'uppercase',
            boxShadow: 'none',

            '&:hover': {
              bgcolor: '#39a53e',
              boxShadow: 'none',
            },

            '&.Mui-disabled': {
              bgcolor: '#acacac',
              color: '#fff',
            },
          }}
        >
          Submit Entry
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaterialsUtilization;
